import { SearchSource } from '@carvana/merch-contracts';
import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useState } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  useInventoryIsLoaded,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import { useLastSearchRequestId } from './hooks';
import {
  useTrackMegaVehicle,
  useTrackMegaUserInfo,
  useTrackSrpFiltersAndTileCounts
} from './hooks/track';

export default function useTrackResults() {
  const { searchSource, keywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const inventoryIsLoaded = useInventoryIsLoaded();
  const { lastSearchRequestId, updateLastSearchRequestId } =
    useLastSearchRequestId();
  const [shouldFire, setShouldFire] = useState(false);
  const searchRequestId = useSearchRequestId();
  const trackEvent = useTrackEvent();
  const trackUserTileInfo = useTrackMegaUserInfo();
  const trackSrpFiltersAndTileCounts = useTrackSrpFiltersAndTileCounts();
  const {
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    trackMegaVehicle
  } = useTrackMegaVehicle();

  useEffect(() => {
    if (lastSearchRequestId.current !== searchRequestId) {
      setShouldFire(true);
    }
  }, [lastSearchRequestId, searchRequestId, svSearchRequestId, setShouldFire]);

  useSubsequentEffect(() => {
    const isStillLoading =
      !loadedInventoryVehicles.isLoaded ||
      (!loadedSimilarVehicles.isLoaded && shouldHaveSimilarVehicles) ||
      !inventoryIsLoaded;

    const isSuggestionSource =
      searchSource === SearchSource.TextSearch ||
      searchSource === SearchSource.RecentSearch ||
      searchSource === SearchSource.SavedSearch;

    if (shouldFire && !isStillLoading) {
      trackMegaVehicle();
      trackUserTileInfo.fireEvent();
      trackSrpFiltersAndTileCounts(loadedInventoryVehicles.vehicles);

      datadogRum.addAction('mega-srp-tile-info', {
        ...trackUserTileInfo.data
      });

      updateLastSearchRequestId(searchRequestId);

      if (isSuggestionSource && !loadedInventoryVehicles.vehicles.length) {
        trackEvent('no-results-from-suggestions', {
          suggestionText: keywordSuggestion?.suggestionText,
          source: searchSource
        });
      }

      if (
        shouldHaveSimilarVehicles &&
        lastSearchRequestId.current !== svSearchRequestId
      ) {
        const similarVehiclesData = {
          ...trackUserTileInfo.data,
          similarVehiclesSearchRequestId: svSearchRequestId,
          vehicles: loadedSimilarVehicles.vehicles
        };

        trackEvent('tile-info-similar-results', similarVehiclesData);
        datadogRum.addAction('tile-info-similar-results', similarVehiclesData);
        updateLastSearchRequestId(svSearchRequestId);
      }

      setShouldFire(false);
    }
  }, [
    inventoryIsLoaded,
    keywordSuggestion,
    lastSearchRequestId,
    loadedInventoryVehicles,
    loadedSimilarVehicles,
    searchRequestId,
    searchSource,
    setShouldFire,
    shouldFire,
    shouldHaveSimilarVehicles,
    svSearchRequestId,
    trackEvent,
    trackMegaVehicle,
    trackSrpFiltersAndTileCounts,
    trackUserTileInfo,
    updateLastSearchRequestId
  ]);
}
